/* ++++++++++ --------------- IMPORTS --------------- ++++++++++ */
// libraries
import React, { useMemo, useCallback } from 'react';
// redux
import {useSelector, useDispatch} from 'react-redux';
// material
import Space from 'material/space/space';
import { SectionGroupV2 } from 'material/expandablesection/v2'
import { SingleSelectDropDown, Switch, ListTable } from '@insticator/insticator-ui'
import { ReactComponent as WarningIcon } from 'assets/icons/error-circle.svg'
// content  
import Emoji from 'pages/dashboard/commenting/commentingconfigv2/sitethemesection/components/emoji/emoji';
// styles
import injectSheet from 'react-jss'
import styles from './emojiselection_styles'
import { setTempCommentingUnitConfig } from 'state/slices/commentingv2';

const emojiOptions = [
  { name: '3D', value: 'THREE_D'},
  { name: 'Flat', value: 'FLAT'},
  { name: 'Monochrome', value: 'MONOCHROME'},
  { name: 'Skeleton', value: 'SKELETON'}
]

const orderedEmojis = ['LIKE', 'DISLIKE', 'LOVE', 'FUNNY', 'SAD', 'ANGRY', 'SHOCKED', 'THOUGHTFUL']
const emojiNamesMap = {
  LIKE: 'Like',
  DISLIKE: 'Dislike',
  LOVE: 'Love',
  FUNNY: 'Funny',
  SAD: 'Sad',
  ANGRY: 'Angry',
  SHOCKED: 'Shocked',
  THOUGHTFUL: 'Thoughtful'
}

const emojiOptionsMap = (name) => {
  return emojiOptions.find(emojiOpt => emojiOpt.value === name) || emojiOptions[0]
}

/* ========== ~~~~~~~~~~ COMMENTING CONFIG V2: SITE THEME SECTION : EMOJI SELECTION ~~~~~~~~~~ ========== */
const EmojiSelection = injectSheet(styles)((props) => {
  const dispatch = useDispatch()
  const { emojiConfigs: emojis } = useSelector(state => state.commentingV2.tempCommentingUnitConfig)

  const { siteTheme, setSiteThemeConfig } = props

  const handleEmojiStyleSelect = (opt) => {
    setSiteThemeConfig('emojiCategoryType', opt.value)
    const updatedEmojis = [...emojis].map(emoji => ({...emoji, style: opt.value}))
    dispatch(setTempCommentingUnitConfig({
      configName: 'emojiConfigs',
      data: [
        ...updatedEmojis
      ]
    }))
  } 

  const enableEmojiToggle = useMemo(() => emojis.length ? emojis.filter(emoji => !!emoji.isActive).length > 2 : false, [emojis])

  const handleEmojiToggle = useCallback((name, value) => { 
    const emojiIndex = [...emojis].findIndex(emoji => emoji.name === name)
    const updatedEmojis = [...emojis]
    updatedEmojis[emojiIndex] = {...updatedEmojis[emojiIndex], isActive: value, active: value}
    dispatch(setTempCommentingUnitConfig({
      configName: 'emojiConfigs',
      data: [
        ...updatedEmojis
      ]
    }))
  }, [dispatch, emojis])

  const mappedEmojis = useMemo(() => emojis ? orderedEmojis.map(orderedEmoji => {
      return emojis.filter(emoji => emoji.name === orderedEmoji)?.pop()
  }) : [], [emojis])

  const emojiCategoryType = useMemo(() => siteTheme.emojiCategoryType || (emojis.length ? emojis.map(emoji => emoji.style)?.pop() : 'THREE_D'), [emojis, siteTheme.emojiCategoryType])

  const emojiList = emojis.length ? mappedEmojis.map((emoji, index) => {
    if(emoji) {
      const name = emoji.name
      const disableToggle = !enableEmojiToggle && emoji.isActive
      return { emoji: <Emoji name={name} type={emojiCategoryType} />, name: emojiNamesMap[name], isActive: <Switch isStatic={disableToggle} on={emoji.isActive} handleClick={() => handleEmojiToggle(name, !emoji.isActive)} />}
    }
  }) : []

  return (
    <SectionGroupV2 
      klass={`emojiselection ${props.classes.emojiselection}`}
      title={'Reactions'} 
      description={<span>Select an emoji style you prefer and which reactions to comments are available.</span>}
      indented
      direction={'column'}
    >
      <Space gap={32} direction={'column'}>
        <SingleSelectDropDown 
          name={emojiOptionsMap(emojiCategoryType).name} 
          searchable={false} 
          onSelect={handleEmojiStyleSelect}
          items={emojiOptions}
        />
        <ListTable 
          name={'table'} 
          type={'standard'} 
          variant={'slim'} 
          size={'full'} 
          columnHeads={
            [
              {
                  "key": "emoji",
                  "title": "Emoji"
              },
              {
                  "key": "name",
                  "title": "Name"
              },
              {
                  "key": "isActive",
                  "title": "Status"
              }
            ]
          }
          data={emojiList}
          rowsPerPage={10} 
          />
          <Space alignItems={'center'}>
            <WarningIcon className='warningicon' /> <p>At least 2 emojis must be enabled</p>
          </Space>
      </Space>
    </SectionGroupV2>
  )
})

/* ++++++++++ --------------- EXPORTS --------------- ++++++++++ */
export default EmojiSelection